import { TruncationTooltip } from 'features/timeSeries/components/TruncationTooltip'
import React, { useCallback, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'

const scrollText = keyframes`
  0%,
  15% {
    transform: translateX(0);
  }

  85%,
  100% {
    transform: translateX(calc(var(--scroll-distance, 0px) * -1));
  }
`

const Viewport = styled.div`
  min-width: 0;
  overflow: hidden;
`

const Track = styled.div<{ $canScroll: boolean }>`
  display: inline-block;
  width: max-content;
  max-width: 100%;
  min-width: 0;
  white-space: nowrap;
  color: inherit;
  font: inherit;
  line-height: inherit;

  ${({ $canScroll }) =>
    $canScroll &&
    css`
      ${Viewport}:hover & {
        animation: ${scrollText} var(--scroll-duration, 6s) ease-in-out infinite
          alternate;
      }
    `}
`

interface HoverScrollableTextProps {
  className?: string
  text: string
  title?: string
}

export const HoverScrollableText: React.FC<HoverScrollableTextProps> = ({
  className,
  text,
  title,
}) => {
  const [scrollDistance, setScrollDistance] = useState(0)
  const [scrollDuration, setScrollDuration] = useState(0)

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const viewport = event.currentTarget
      const track = viewport.firstElementChild

      if (!(track instanceof HTMLElement)) {
        setScrollDistance(0)
        setScrollDuration(0)
        return
      }

      const overflowAmount = Math.ceil(track.scrollWidth - viewport.clientWidth)

      if (overflowAmount <= 0) {
        setScrollDistance(0)
        setScrollDuration(0)
        return
      }

      setScrollDistance(overflowAmount)
      setScrollDuration(Math.max(4, overflowAmount / 18))
    },
    []
  )

  // Truncation-gated: text that fits its viewport gets no tooltip at all.
  return (
    <TruncationTooltip
      label={title ?? text}
      style={{ minWidth: 0, display: 'flex', flex: 1 }}>
      <Viewport className={className} onMouseEnter={handleMouseEnter}>
        <Track
          $canScroll={scrollDistance > 0}
          style={
            {
              '--scroll-distance': `${scrollDistance}px`,
              '--scroll-duration': `${scrollDuration}s`,
            } as React.CSSProperties
          }>
          {text}
        </Track>
      </Viewport>
    </TruncationTooltip>
  )
}
